<template>
  <loader v-if="loading" :show="loading" fullscreen />
  <not-working
    v-else-if="!operatorsNum"
    :show="true"
    text="Gompels Quick Collect is not currently available, please join the queue."
  />
  <div v-else class="main-layout">
    <div class="content-box">
      <div class="text-center">
        <b-img class="logo" src="/static/img/gompels_logo.png" />
        <div v-if="isResponse && isCompletedPrescription" class="response-box">
          <h2>Your prescription was collected on:</h2>
          <h1 class="collection-message">{{ completedOnFormatted }}</h1>
          <h4>Please ask the staff if you have any questions.</h4>
        </div>
        <div v-else-if="isResponse" class="response-box">
          <h2 class="font-weight-bold">Your Collection Number is:</h2>
          <h1 class="collection-number">{{ collectionNumber }}</h1>
          <h2 class="font-weight-bold">Please take a seat and wait to be called.</h2>
          <h2 class="gompels-text">Thanks for using Gompels Collect.</h2>
        </div>
        <div v-else>
          <h1 class="title">QUICK COLLECT</h1>
          <h4 class="subtitle">Please scan or key in your collection code and a member of staff will get your prescription for you.</h4>
        </div>
      </div>
      <div v-if="isResponse" class="action-box">
        <b-button variant="outline-success" size="lg" @click="onScanNewCodeClick">
          Scan New Code
        </b-button>
      </div>
      <div v-else class="input-box">
        <b-input-group class="input-group">
          <b-form-input
            class="patient-input"
            v-model="wroteBarcode"
            placeholder="Enter Code"
            size="lg"
            @input="tmpBarcode = ''"
            @focus="removeScanBarcode"
            @blur="addScanBarcode"
            @keydown.enter="onSendClick(wroteBarcode)"
          />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="onSendClick(wroteBarcode)">
              <i class="fas fa-paper-plane input-icon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <h4 class="or-text">OR</h4>
        <b-img alt="" class="scan-image" src="/static/img/arrow.png" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useSettingStore } from '@/store/settings';
import { usePatientStore } from '@/store/patient';
import NotWorking from '@/core/components/not-working/NotWorking';
import Loader from '@/core/components/loader/loader';

export default {
  name: 'Patient',
  components: {Loader, NotWorking},
  data() {
    return {
      operatorsNum: null,
      scannedBarcode: '',
      wroteBarcode: '',
      isResponse: false,
      completedOn: null,
      collectionNumber: null,
      tmpBarcode: '',
      hasScannerListener: false,
      timer: null,
    };
  },
  computed: {
    ...mapState(useSettingStore, ['settingsList', 'loading']),
    isCompletedPrescription() {
      return !!this.completedOn;
    },
    completedOnFormatted() {
      if (!this.completedOn) return ''
      const dateObj = new Date(this.completedOn)
      const day = dateObj.getDate()
      const monthName = dateObj.toLocaleString('default', { month: 'long' })
      const year = dateObj.getFullYear()
      const hours = String(dateObj.getHours()).padStart(2, '0')
      const minutes = String(dateObj.getMinutes()).padStart(2, '0')
      return `${day} ${monthName} ${year}  ${hours}:${minutes}`
    }
  },
  methods: {
    ...mapActions(usePatientStore, ['createPrescription']),
    ...mapActions(useSettingStore, ['getSettingsList']),
    addScanBarcode() {
      if (!this.hasScannerListener) {
        document.addEventListener('keydown', this.onKeyPress);
      }
      this.hasScannerListener = true;
    },
    removeScanBarcode() {
      if (this.hasScannerListener) {
        document.removeEventListener('keydown', this.onKeyPress);
      }
      this.hasScannerListener = false;
    },
    async onSendClick(barcode) {
      if (!barcode) {
        return;
      }
      let normalizedBarcode = barcode.toUpperCase();
      if (normalizedBarcode.length === 6) {
      } else if (normalizedBarcode.length > 6) {
        if (normalizedBarcode.startsWith('$CC')) {
          normalizedBarcode = normalizedBarcode.slice(3);
          if (normalizedBarcode.length !== 6) {
            this.$toast('danger', 'Barcode must be 6 symbols after removing "$CC".');
            return;
          }
        } else {
          this.$toast('danger', 'Invalid barcode prefix. Expected "$CC".');
          return;
        }
      } else {
        this.$toast('danger', 'Barcode must be 6 symbols.');
        return;
      }
      const { success, body } = await this.createPrescription({ barcode: normalizedBarcode });
      if (success) {
        if (body.current_status === 3) {
          this.isResponse = true;
          this.completedOn = body.completed_on;
        } else {
          this.isResponse = true;
          this.collectionNumber = body.id_number;
          this.completedOn = null;
        }
        this.wroteBarcode = '';
        this.removeScanBarcode();
        this.timer = setTimeout(() => {
          this.isResponse = false;
          this.collectionNumber = null;
          this.addScanBarcode();
        }, 7000);
      } else {
        this.wroteBarcode = '';
        this.$toast('danger', body.barcode[0]);
      }
    },
    async onKeyPress($event) {
      if ($event.code === 'Enter' || $event.code === 'NumpadEnter' || $event.which === 13) {
        if (this.tmpBarcode) {
          await this.onSendClick(this.tmpBarcode);
        }
        this.tmpBarcode = '';
        return;
      }
      if (($event.which >= 48 && $event.which <= 90) || ($event.which >= 96 && $event.which <= 105)) {
        this.tmpBarcode += $event.key;
        this.wroteBarcode = this.tmpBarcode;
      }
    },
    onScanNewCodeClick() {
      this.isResponse = false;
      this.collectionNumber = null;
      this.addScanBarcode();
      clearTimeout(this.timer);
    },
  },
  async mounted() {
    await this.getSettingsList();
    this.operatorsNum = this.settingsList.number_of_operators;
    if (this.operatorsNum > 0) {
      this.addScanBarcode();
    }
    this.$ws.connect();
    this.$ws.listen('CHANGE_NUMBER_OF_OPERATORS', (data) => {
      this.operatorsNum = data.number_of_operators;
      if (this.operatorsNum > 0) {
        this.addScanBarcode();
      } else {
        this.removeScanBarcode();
      }
    });
  },
  destroyed() {
    this.$ws.remove('CHANGE_NUMBER_OF_OPERATORS');
  },
}
</script>

<style lang="scss" scoped>
.main-layout {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: #0A8544;
}
.content-box {
  width: 100%;
  max-width: 90vw;
  height: 100%;
  max-height: 95vh;
  padding: 4vh 3vw;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  text-align: center;
}
.logo {
  height: 14vh;
  margin-bottom: 2vh;
}
.title {
  font-weight: bold;
  font-size: 8vh;
  margin-bottom: 2vh;
}
.subtitle {
  font-size: 4vh;
  margin-bottom: 3vh;
}
.response-box {
  padding: 8vh 0;
}
.collection-message {
  color: #0A8544;
  font-size: 8vh;
  font-weight: bold;
}
.collection-number {
  color: #0A8544;
  font-size: 10vh;
  font-weight: bold;
}
.action-box {
  margin-top: 3vh;
}
.input-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 3vh;
}
.scan-image {
  display: block;
  width: auto;
  max-width: auto;
  height: auto;
  max-height: 33vh;
  object-fit: contain;
}
.or-text {
  margin: 1vh 0;
  font-size: 4vh;
  font-family: "Malgun Gothic", Arial, sans-serif;
}
.input-group {
  width: 40%;
}
.patient-input {
  text-transform: uppercase;
  font-size: 2rem;
  padding: 1rem;
  height: auto;
}

.input-icon {
  font-size: 2rem;
  padding: 0.5rem;
}

.gompels-text {
  font-weight: bold;
  color: #0A8544;
}
</style>
